import {
  TagAutocompleteFilterWrapper,
  FilterType,
  FoodTreeSelectFilterWrapper,
  MultiSelectFilterWrapper,
  OnlyPolishFilterWrapper,
  SingleAutocompleteFilterWrapper,
  SingleSelectChipFilterWrapper,
  SingleSelectFilterWrapper,
  SliderFilterWrapper,
  AutocompleteFilterWrapper,
} from "@components/Filters";
import { useClinicWhitelabel } from "./useClinicWhitelabel";
import { useAppTranslation } from "./useAppTranslation";
import { FieldValues, Path } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { PRODUCTS, SECTIONS } from "@routes";

export const useListTabs = () => {
  const { t } = useAppTranslation();
  const { pathname } = useLocation();
  const { isMotherClinic, isPartnerClinic, getMotherClinicName } =
    useClinicWhitelabel();
  const isProductsView = pathname.startsWith(PRODUCTS);

  const conditionalTabs = [
    {
      label: t("filters.tabs.own"),
      id: ListTabs.OWN,
    },
    {
      label: pathname.includes(SECTIONS) ? t("common.templates") : "alloweat",
      id: ListTabs.ALLOWEAT,
    },
    ...(isPartnerClinic
      ? [{ label: t("filters.tabs.common_clinic"), id: ListTabs.COMMON_CLINIC }]
      : []),
    ...(isMotherClinic
      ? [
          {
            label: getMotherClinicName(t("common.clinic")),
            id: ListTabs.MOTHER_CLINIC,
          },
        ]
      : []),
  ];

  const regularTabs = [
    {
      label: t("filters.tabs.all"),
      id: ListTabs.ALL,
    },
    {
      label: t("filters.tabs.latest"),
      id: ListTabs.LATEST,
    },
    {
      label: t("filters.tabs.favourite"),
      id: ListTabs.FAVORITE,
    },
    ...conditionalTabs,
  ];

  const adminTabs = [
    ...regularTabs,
    ...(isProductsView
      ? [
          {
            label: t("filters.tabs.generic"),
            id: ListTabs.GENERIC,
          },
          {
            label: t("filters.tabs.synonyms"),
            id: ListTabs.SYNONYMS,
          },
        ]
      : []),
    {
      label: t("filters.tabs.unverified"),
      id: ListTabs.UNVERIFIED,
    },
  ];

  const unshiftListTabFilter = <T extends FieldValues>(
    list: (
      | TagAutocompleteFilterWrapper
      | AutocompleteFilterWrapper<T>
      | SingleAutocompleteFilterWrapper<T>
      | MultiSelectFilterWrapper<T>
      | SingleSelectFilterWrapper<T>
      | SliderFilterWrapper<T>
      | OnlyPolishFilterWrapper<T>
      | FoodTreeSelectFilterWrapper<T>
      | SingleSelectChipFilterWrapper<T>
    )[],
    label: string,
    isAdmin?: boolean,
  ): (
    | TagAutocompleteFilterWrapper
    | AutocompleteFilterWrapper<T>
    | SingleAutocompleteFilterWrapper<T>
    | MultiSelectFilterWrapper<T>
    | SingleSelectFilterWrapper<T>
    | SliderFilterWrapper<T>
    | OnlyPolishFilterWrapper<T>
    | FoodTreeSelectFilterWrapper<T>
    | SingleSelectChipFilterWrapper<T>
  )[] => {
    return [
      {
        type: FilterType.SingleSelectChip,
        props: {
          label,
          options: isAdmin ? adminTabs : regularTabs,
          name: "tabs" as Path<T>,
        },
      },
      ...list,
    ];
  };

  return {
    regularTabs,
    adminTabs,
    unshiftListTabFilter,
  };
};

export enum ListTabs {
  ALL = "all",
  LATEST = "latest",
  FAVORITE = "favorite",
  OWN = "own",
  GENERIC = "generic",
  SYNONYMS = "synonyms",
  UNVERIFIED = "reviewed",
  ALLOWEAT = "alloweat",
  COMMON_CLINIC = "commonClinic",
  MOTHER_CLINIC = "motherClinic",
}

export enum SourceTabs {
  ALLOWEAT = "1",
  CLINIC = "2",
  COMMON_CLINIC = "3",
  MOTHER_CLINIC = "4",
}

export const getSourceTabFromListTab = (
  listTab: ListTabs,
): SourceTabs | null => {
  const mapping: Partial<Record<ListTabs, SourceTabs>> = {
    [ListTabs.ALLOWEAT]: SourceTabs.ALLOWEAT,
    [ListTabs.COMMON_CLINIC]: SourceTabs.COMMON_CLINIC,
    [ListTabs.MOTHER_CLINIC]: SourceTabs.MOTHER_CLINIC,
    [ListTabs.OWN]: SourceTabs.CLINIC,
  };

  return mapping[listTab] ?? null;
};
